"use client";

import { useEffect, useState, useCallback } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { api } from "@/lib/api";
import { getSession } from "@/lib/auth";
import type { Job, Contractor } from "@/lib/types";
import { STATUS_COLORS, STATUS_LABELS } from "@/lib/types";
import { formatEstimateRange, formatJobFinalPrice, hasSelectedContractorPrice } from "@/lib/pricing";
import { motion, AnimatePresence } from "framer-motion";
import { JobDetails } from "@/components/JobDetails";
import { 
  Plus, 
  Activity, 
  CheckCircle2, 
  Clock, 
  ChevronRight, 
  Star, 
  Phone, 
  MapPin, 
  ShieldCheck, 
  Wrench,
  AlertCircle,
  TrendingUp,
  LayoutDashboard,
  Calendar,
  Sparkles,
  Mail,
  CreditCard,
  User as UserIcon,
  Settings
  ,Repeat2
  ,Trash2
  ,Pencil
  ,Image as ImageIcon
  ,X
  ,FileVideo

} from "lucide-react";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { MediaUploader } from "@/components/ui/MediaUploader";
import { ReviewModal } from "@/components/ReviewModal";
import dynamic from "next/dynamic";
import { JobChat } from "@/components/chat/JobChat";
import { ContractorProfileModal } from "@/components/ContractorProfileModal";
import { ChatNotificationBell } from "@/components/chat/ChatNotificationBell";

const LiveTrackingMap = dynamic(() => import("@/components/LiveTrackingMap").then(mod => mod.LiveTrackingMap), { 
  ssr: false,
  loading: () => <div className="h-[450px] bg-slate-50 rounded-[2.5rem] animate-pulse" />
});

type TabId = "overview" | "history" | "profile";

export default function DashboardPage() {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [user, setUser] = useState<{ name: string; phone: string; email: string } | null>(null);
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", phone: "" });
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showReview, setShowReview] = useState<string | null>(null);
  const [userName, setUserName] = useState("");

  const load = useCallback(async () => {
    try {
      const data = (await api.getMyJobs()) as Job[];
      setJobs(data);
    } catch (e: any) {
      if (e.message?.includes("401") || e.message?.includes("Unauthorized")) {
        router.push("/login");
      }
    } finally { setLoading(false); }
  }, [router]);

  useEffect(() => {
    const session = getSession();
    if (!session || session.role !== "customer" || !session.token) {
      router.push("/login");
      return;
    }
    setUserName(session.user?.name || "Member");
    load();
    loadProfile();
  }, [router, load]);

  async function loadProfile() {
    try {
      const data = await api.me() as any;
      setUser(data);
      setEditForm({ name: data.name || "", phone: data.phone || "" });
    } catch (e) { console.error(e); }
  }

  async function updateProfile() {
    setUpdatingProfile(true);
    try {
      await api.updateMe(editForm);
      await loadProfile();
      alert("Profile updated successfully!");
    } catch (e: any) { alert(e.message); }
    finally { setUpdatingProfile(false); }
  }

  useEffect(() => {
    const hasOpen = jobs.some(j => ["priced", "assigned", "manual_dispatch_required", "awaiting_customer_confirmation"].includes(j.status));
    if (!hasOpen) return;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [jobs, load]);

  async function handleConfirm(id: string) {
    setActionLoading(id + "_confirm");
    try { 
      await api.confirmCompletion(id); 
      setShowReview(id); 
      await load(); 
    } finally { setActionLoading(null); }
  }

  async function handleReview(id: string, rating: number, comment: string) {
    setActionLoading(id + "_review");
    try { 
      await api.reviewJob(id, rating, comment); 
      setShowReview(null); 
      await load(); 
    } finally { setActionLoading(null); }
  }

  async function handleApprovePart(jobId: string, partId: string) {
    setActionLoading(jobId + "_" + partId + "_approve");
    try {
      await api.approveJobPart(jobId, partId);
      await load();
    } finally { setActionLoading(null); }
  }

  async function handleRejectPart(jobId: string, partId: string) {
    setActionLoading(jobId + "_" + partId + "_reject");
    try {
      await api.rejectJobPart(jobId, partId);
      await load();
    } finally { setActionLoading(null); }
  }
  async function handlePayment(jobId: string) {
    setActionLoading(jobId + "_payment");
    try {
      const { url } = await api.createCheckoutSession(jobId) as { url: string };
      window.location.href = url;
    } catch (e: any) { alert(e.message); }
    finally { setActionLoading(null); }
  }

  async function handleSelectBid(jobId: string, bidId: string) {
    setActionLoading(jobId + "_" + bidId + "_select");
    try {
      await api.selectJobBid(jobId, bidId);
      await load();
    } catch (e: any) { alert(e.message || "Could not select contractor bid"); }
    finally { setActionLoading(null); }
  }

  async function handleRepost(jobId: string) {
    setActionLoading(jobId + "_repost");
    try {
      await api.repostJob(jobId);
      await load();
    } catch (e: any) {
      alert(e.message || "Could not repost this job");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleEditJob(jobId: string, body: { description?: string; images?: string[]; videos?: string[] }) {
    setActionLoading(jobId + "_edit");
    try {
      await api.updateJob(jobId, body);
      await load();
    } catch (e: any) {
      alert(e.message || "Could not update this job");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDeleteJob(jobId: string, silent = false) {
    if (!silent) {
      const confirmDelete = window.confirm("Are you sure you want to cancel and delete this service request?");
      if (!confirmDelete) return;
    }
    setActionLoading(jobId + "_delete");
    try {
      await api.deleteJob(jobId);
      await load();
    } catch (e: any) {
      alert(e.message || "Could not delete this job");
    } finally {
      setActionLoading(null);
    }
  }

  if (loading) return (
    <main className="min-h-screen bg-mesh flex items-center justify-center">
      <div className="flex flex-col items-center gap-6">
        <div className="relative">
          <div className="h-16 w-16 border-4 border-emerald-600/10 border-t-emerald-600 rounded-full animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center">
            <ShieldCheck size={24} className="text-emerald-600 animate-pulse" />
          </div>
        </div>
        <div className="text-center">
          <p className="text-sm font-black text-slate-900 uppercase tracking-[0.2em]">Synchronizing Portfolio</p>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-2 px-6 py-1 bg-white border border-slate-100 rounded-full">Fixer Verified Registry v3.1</p>
        </div>
      </div>
    </main>
  );

  const activeJobs = jobs.filter(j => !["reviewed", "cancelled"].includes(j.status));
  const doneJobs = jobs.filter(j => j.status === "reviewed");
  const responseRequiredJob = jobs.find(j => j.status === "response_required");
  const stats = [
    { label: "Active Requests", value: activeJobs.length, icon: <Activity size={18} />, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Completed Jobs", value: doneJobs.length, icon: <CheckCircle2 size={18} />, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Account Status", value: "Verified", icon: <ShieldCheck size={18} />, color: "text-amber-600", bg: "bg-amber-50" },
  ];

  return (
    <main className="min-h-screen bg-mesh pb-24 pt-32 font-sans antialiased">
      {/* Dynamic Review Modal */}
      {showReview && (() => {
        const currentJob = jobs.find(j => j.id === showReview);
        return (
          <ReviewModal 
            isOpen={!!showReview}
            onClose={() => setShowReview(null)}
            onSubmit={(r, c) => handleReview(showReview, r, c)}
            jobTitle={currentJob?.description || "Service Job"}
            contractorName={currentJob?.contractor?.name || "Professional"}
            paymentStatus={currentJob?.paymentStatus}
            quotedPrice={currentJob?.quotedPrice}
            onPay={() => handlePayment(showReview)}
          />
        );
      })()}

      <AnimatePresence>
        {responseRequiredJob && (
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-950/55 backdrop-blur-md"
            />
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.97 }}
              className="relative w-full max-w-xl overflow-hidden rounded-[2rem] border border-white/70 bg-white shadow-[0_32px_100px_rgba(15,23,42,0.3)]"
            >
              <div className="border-b border-slate-100 bg-rose-50/70 px-6 py-5 sm:px-8">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-rose-600 shadow-sm">
                    <Clock size={22} />
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-rose-600">Response window ended</p>
                    <h2 className="mt-1 text-xl font-black text-slate-950">No contractor bid was received</h2>
                  </div>
                </div>
              </div>

              <div className="p-6 sm:p-8">
                <p className="text-sm leading-relaxed text-slate-600">
                  Your <strong className="text-slate-950">{responseRequiredJob.category}</strong> request in{" "}
                  <strong className="text-slate-950">{responseRequiredJob.location}</strong> did not receive a response within the selected{" "}
                  <strong className="text-slate-950">{responseRequiredJob.urgency}</strong> window.
                </p>

                <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <JobDetails description={responseRequiredJob.description} compact />
                  <div className="mt-4 flex flex-wrap items-center gap-3 text-xs font-bold text-slate-500">
                    <span className="rounded-full bg-white px-3 py-1.5">#{responseRequiredJob.id.slice(-6).toUpperCase()}</span>
                    <span className="rounded-full bg-white px-3 py-1.5">{formatEstimateRange(responseRequiredJob)}</span>
                  </div>
                </div>

                <p className="mt-6 text-sm font-semibold text-slate-700">Would you like to notify matching contractors again?</p>
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <button
                    onClick={() => handleDeleteJob(responseRequiredJob.id, true)}
                    disabled={!!actionLoading}
                    className="flex items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-white px-5 py-4 text-sm font-black text-rose-600 transition-all hover:bg-rose-50 disabled:opacity-50"
                  >
                    <Trash2 size={17} />
                    {actionLoading === responseRequiredJob.id + "_delete" ? "Deleting..." : "No, delete request"}
                  </button>
                  <button
                    onClick={() => handleRepost(responseRequiredJob.id)}
                    disabled={!!actionLoading}
                    className="flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-4 text-sm font-black text-white shadow-lg shadow-emerald-200 transition-all hover:bg-emerald-700 disabled:opacity-50"
                  >
                    <Repeat2 size={17} />
                    {actionLoading === responseRequiredJob.id + "_repost" ? "Reposting..." : "Repost job"}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="mx-auto max-w-6xl px-6">
        {/* Header */}
        <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em]">Property Command</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tight text-slate-950 leading-[0.9]">
              Welcome to Fixer Local, {userName.split(" ")[0]}
            </h1>
            <p className="mt-4 text-slate-500 font-medium max-w-sm leading-relaxed">
              Your real-time nexus for property integrity, maintenance intelligence, and service optimization.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex flex-col items-start md:items-end gap-4 w-full md:w-auto"
          >
            <div className="flex items-center gap-2 p-1.5 bg-slate-100 rounded-2xl border border-slate-200 flex-wrap">
              {(["overview", "history", "profile"] as TabId[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === tab ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-4 flex-wrap">
              <ChatNotificationBell />
              <Link 
                href="/dashboard/analysis" 
                className="h-14 w-14 rounded-2xl bg-white border border-slate-200 text-slate-600 hover:text-emerald-600 hover:border-emerald-100 transition-all shadow-sm flex items-center justify-center active:scale-95 group"
                title="System Analytics"
              >
                <Activity size={20} className="group-hover:scale-110 transition-transform" />
              </Link>
              <Link 
                href="/booking" 
                className="flex items-center justify-center gap-3 rounded-2xl bg-white border border-slate-200 px-8 h-14 text-sm font-black text-slate-900 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all shadow-sm active:scale-95 uppercase tracking-widest"
              >
                <Plus size={20} />
                Get a Quote
              </Link>
            </div>
          </motion.div>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === "overview" && (
            <motion.div 
              key="overview"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
            >
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                {stats.map((stat, i) => (
                  <div
                    key={stat.label}
                    className="glass p-6 rounded-[2rem] border border-white/50 shadow-sm flex items-center justify-between group hover:border-white transition-all bg-white/40"
                  >
                    <div>
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{stat.label}</p>
                      <p className="text-2xl font-black text-slate-950">{stat.value}</p>
                    </div>
                    <div className={`h-12 w-12 rounded-2xl ${stat.bg} ${stat.color} flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform`}>
                      {stat.icon}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                <div className="lg:col-span-2 space-y-12">
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                        <Activity size={18} className="text-emerald-500" />
                        Active Service Requests
                      </h2>
                      {activeJobs.length > 0 && <span className="h-2 w-2 rounded-full bg-emerald-500 animate-ping" />}
                    </div>

                    {activeJobs.length === 0 ? (
                      <div className="rounded-[2.5rem] border border-dashed border-slate-300 bg-white/50 p-16 text-center">
                        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-[1.5rem] bg-slate-100 text-slate-400">
                          <Wrench size={32} />
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 mb-2 italic">Systems Clear</h3>
                        <p className="text-sm text-slate-500 font-medium max-w-[240px] mx-auto leading-relaxed">
                          No active maintenance protocols detected in your current portfolio.
                        </p>
                        <Link href="/booking" className="mt-8 inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-emerald-600 hover:text-emerald-500 transition-colors">
                          Initiate New Request
                          <ChevronRight size={16} />
                        </Link>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {activeJobs.map((job) => (
                          <JobCard 
                            key={job.id} 
                            job={job} 
                            actionLoading={actionLoading} 
                            showReview={showReview}
                            onConfirm={handleConfirm} 
                            onApprovePart={handleApprovePart}
                            onRejectPart={handleRejectPart}
                            onPay={handlePayment}
                            onSelectBid={handleSelectBid}
                            onDelete={handleDeleteJob}
                            onEdit={handleEditJob}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="glass p-8 rounded-[2.5rem] border border-white/50 shadow-sm bg-white/40">
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest mb-6">Support Center</h3>
                    <div className="space-y-4">
                      <Link href="/help#safety" className="flex gap-4 p-4 rounded-2xl bg-white/40 hover:bg-white/60 transition-colors border border-transparent hover:border-emerald-100 cursor-pointer group">
                        <div className="h-10 w-10 shrink-0 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                          <ShieldCheck size={20} />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-slate-950 group-hover:text-emerald-700 transition-colors">Safety Standard</h4>
                          <p className="text-xs text-slate-500 font-medium mt-1">Contractors verify their business email before going live - check licensing/insurance directly with your pro.</p>
                        </div>
                      </Link>
                    </div>

                    <div className="mt-6">
                      <Link href="/contact" className="inline-flex px-4 py-2.5 rounded-xl bg-emerald-600 text-white items-center gap-2 hover:bg-emerald-700 transition-all active:scale-[0.98]">
                        <Mail size={14} />
                        <span className="text-[10px] font-black uppercase tracking-widest">Email Support</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === "history" && (
            <motion.div 
              key="history"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="space-y-8"
            >
              <div className="mb-8">
                <h2 className="text-3xl font-black text-slate-950 tracking-tighter mb-2">Service History</h2>
                <p className="text-slate-500 font-medium italic">Archived maintenance records and completed property interventions.</p>
              </div>

              {doneJobs.length === 0 ? (
                <div className="rounded-[2.5rem] border border-dashed border-slate-300 bg-white/50 p-32 text-center">
                  <h3 className="text-lg font-bold text-slate-400 italic uppercase tracking-widest">No history recorded</h3>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {doneJobs.map(job => (
                    <JobCard 
                      key={job.id} 
                      job={job} 
                      actionLoading={actionLoading} 
                      showReview={showReview} 
                      onConfirm={handleConfirm} 
                      onApprovePart={handleApprovePart}
                      onRejectPart={handleRejectPart}
                      onPay={handlePayment}
                      onSelectBid={handleSelectBid}
                      onDelete={handleDeleteJob}
                      onEdit={handleEditJob}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {activeTab === "profile" && (
            <motion.div 
              key="profile"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="max-w-4xl"
            >
              <div className="mb-12">
                <h2 className="text-4xl font-black text-slate-950 tracking-tighter mb-2">Property Owner Identity</h2>
                <p className="text-slate-500 font-medium italic">Manage your account credentials and personal communication channels.</p>
              </div>

              <div className="bg-white rounded-[40px] border border-slate-200 overflow-hidden shadow-sm">
                <div className="p-12 space-y-12">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    <div className="space-y-6">
                      <div className="space-y-4">
                        <label className="text-xs font-black uppercase tracking-widest text-slate-400">Account Nodes</label>
                        <div className="space-y-4">
                          <div className="p-6 bg-slate-50 border border-slate-100 rounded-[22px]">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Email Address (Immutable)</p>
                            <p className="text-sm font-bold text-slate-400">{user?.email}</p>
                          </div>
                          
                          <div className="space-y-2">
                             <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Personal Identity</p>
                             <input 
                                type="text" 
                                value={editForm.name}
                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                placeholder="Full Name"
                                className="w-full h-16 bg-slate-50 border border-slate-200 rounded-[22px] px-6 text-sm font-bold text-slate-950 outline-none focus:ring-4 focus:ring-emerald-50 focus:border-emerald-500 transition-all"
                             />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="space-y-4">
                        <label className="text-xs font-black uppercase tracking-widest text-slate-400">Communication Channels</label>
                        <div className="space-y-2">
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Verified Phone Network</p>
                          <PhoneInput 
                            value={editForm.phone}
                            onChange={(val) => setEditForm({ ...editForm, phone: val })}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-8 border-t border-slate-100 flex justify-end">
                    <button 
                      onClick={updateProfile}
                      disabled={updatingProfile}
                      className="px-10 py-5 bg-emerald-600 text-white rounded-[22px] font-black uppercase tracking-widest hover:bg-emerald-600 transition-all shadow-xl shadow-slate-900/10 active:scale-95 disabled:opacity-50 flex items-center gap-3"
                    >
                      {updatingProfile ? <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Settings size={18} />}
                      Commit Secure Updates
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}

function JobCard({ job, actionLoading, showReview, onConfirm, onApprovePart, onRejectPart, onPay, onSelectBid, onDelete, onEdit }: {
  job: Job; actionLoading: string | null; showReview: string | null;
  onConfirm: (id: string) => void;
  onApprovePart: (jobId: string, partId: string) => void;
  onRejectPart: (jobId: string, partId: string) => void;
  onPay: (jobId: string) => void;
  onSelectBid: (jobId: string, bidId: string) => void;
  onDelete: (id: string) => void;
  onEdit: (jobId: string, body: { description?: string; images?: string[]; videos?: string[] }) => void;
}) {
  const isDone = job.status === "reviewed" || job.status === "cancelled";
  const showFinalPrice = hasSelectedContractorPrice(job);
  const [chatOpen, setChatOpen] = useState(false);
  const [viewingContractor, setViewingContractor] = useState<Contractor | null>(null);
  // Editable while the job is still open (no contractor picked yet).
  const canEdit = !job.contractorId && job.status !== "cancelled";
  const [isEditing, setIsEditing] = useState(false);
  const [editDescription, setEditDescription] = useState(job.description);
  const [editImages, setEditImages] = useState<string[]>(job.images || []);

  function saveEdit() {
    onEdit(job.id, { description: editDescription, images: editImages });
    setIsEditing(false);
  }

  function cancelEdit() {
    setEditDescription(job.description);
    setEditImages(job.images || []);
    setIsEditing(false);
  }

  return (
    <div className={`glass rounded-[2rem] border transition-all duration-300 overflow-hidden ${
      isDone ? "border-slate-100 opacity-80" : "border-white/50 shadow-md shadow-slate-900/5 bg-white/80"
    }`}>
      <div className="p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border ${
                STATUS_COLORS[job.status] || "bg-slate-50 text-slate-600 border-slate-200"
              }`}>
                {STATUS_LABELS[job.status] || job.status}
              </span>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">#{job.id.slice(-6)}</span>
            </div>
            {isEditing ? (
              <div className="mb-4 space-y-4">
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  className="w-full rounded-2xl border border-slate-200 p-4 text-sm text-slate-700 font-medium focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-400"
                />
                <MediaUploader
                  label="Photos or videos"
                  initialUrls={editImages}
                  onUploadComplete={setEditImages}
                  maxFiles={6}
                />
                <div className="flex gap-3">
                  <button
                    onClick={saveEdit}
                    disabled={!!actionLoading}
                    className="px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest hover:bg-emerald-700 transition-all disabled:opacity-50"
                  >
                    {actionLoading === job.id + "_edit" ? "Saving..." : "Save Changes"}
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="px-5 py-2.5 rounded-xl bg-slate-100 text-slate-600 text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <JobDetails description={job.description} />
            )}
            <div className="flex flex-wrap items-center gap-y-2 gap-x-4 text-sm text-slate-500 font-medium">
              <div className="flex items-center gap-1.5"><MapPin size={14} className="text-slate-400" /> {job.location}</div>
              <div className="flex items-center gap-1.5"><Clock size={14} className="text-slate-400" /> {job.urgency}</div>
              <div className="flex items-center gap-1.5"><Wrench size={14} className="text-slate-400" /> <span className="capitalize">{job.category}</span></div>
            </div>

            {!isEditing && ((job.images && job.images.length > 0) || (job.videos && job.videos.length > 0)) && (
              <div className="flex flex-wrap gap-2 mt-4">
                {(job.images || []).map((url, i) => (
                  <a key={`img-${i}`} href={url} target="_blank" rel="noopener noreferrer" className="h-16 w-16 rounded-xl overflow-hidden border border-slate-200 shrink-0 hover:ring-2 hover:ring-emerald-300 transition-all">
                    <img src={url} alt={`Attachment ${i + 1}`} className="h-full w-full object-cover" />
                  </a>
                ))}
                {(job.videos || []).map((url, i) => (
                  <a key={`vid-${i}`} href={url} target="_blank" rel="noopener noreferrer" className="h-16 w-16 rounded-xl overflow-hidden border border-slate-200 shrink-0 bg-slate-900 flex items-center justify-center text-white hover:ring-2 hover:ring-emerald-300 transition-all">
                    <FileVideo size={22} />
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col md:items-end gap-1">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">{showFinalPrice ? "Final Service Price" : "Estimate Range"}</p>
            <p className="text-3xl font-black text-slate-950">{showFinalPrice ? formatJobFinalPrice(job) : formatEstimateRange(job)}</p>
            {showFinalPrice && (
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Estimate {formatEstimateRange(job)}</p>
            )}
            <div className="flex flex-col items-end gap-2">
              {job.parts && job.parts.length > 0 && (
                <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest mt-1">
                  Incl. ${job.parts.filter(p => p.status === 'APPROVED').reduce((s, p) => s + p.price, 0)} Parts
                </p>
              )}
              {job.paymentStatus === "paid" ? (
                <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-[9px] font-black uppercase tracking-widest border border-emerald-100 flex items-center gap-1">
                  <ShieldCheck size={10} /> Fully Paid
                </span>
              ) : (
                <button 
                  onClick={() => onPay(job.id)}
                  disabled={!!actionLoading}
                  className="mt-2 px-6 py-2.5 bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase tracking-[0.15em] hover:bg-emerald-600 transition-all shadow-xl shadow-slate-200 flex items-center gap-2 active:scale-95 disabled:opacity-50"
                >
                  <CreditCard size={14} /> 
                  {actionLoading === job.id + "_payment" ? "Redirecting..." : "Pay Now"}
                </button>
              )}

              {/* Edit Request button - only while no contractor has been picked yet */}
              {canEdit && !isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="mt-2 w-full px-6 py-2.5 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-xl text-[10px] font-black uppercase tracking-[0.15em] transition-all flex items-center justify-center gap-2 active:scale-95 shadow-sm"
                >
                  <Pencil size={12} />
                  Edit Request
                </button>
              )}

              {/* Cancel Request button */}
              {!job.contractorId && job.status !== "completed" && job.status !== "reviewed" && job.status !== "cancelled" && (
                <button
                  onClick={() => onDelete(job.id)}
                  disabled={!!actionLoading}
                  className="mt-2 w-full px-6 py-2.5 bg-rose-50 text-rose-600 hover:bg-rose-500 hover:text-white rounded-xl text-[10px] font-black uppercase tracking-[0.15em] transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 shadow-sm"
                >
                  <Trash2 size={12} />
                  {actionLoading === job.id + "_delete" ? "Cancelling..." : "Cancel Request"}
                </button>
              )}
            </div>
          </div>
        </div>

        {!job.contractorId && job.bids && job.bids.length > 0 && (
          <div className="mt-8 rounded-[2rem] bg-white border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-5">
              <h4 className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Contractor Bids</h4>
              <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">{job.bids.filter(b => b.status === "pending").length} available</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {job.bids.filter(bid => bid.status === "pending").map(bid => (
                <div key={bid.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
                  <div className="flex items-start justify-between gap-4">
                    <button
                      type="button"
                      onClick={() => bid.contractor && setViewingContractor(bid.contractor)}
                      className="flex items-start gap-3 min-w-0 text-left hover:opacity-80 transition-opacity"
                    >
                      {bid.contractor?.logo_url ? (
                        <img src={bid.contractor.logo_url} alt={bid.contractor.name} className="h-11 w-11 rounded-xl object-cover border border-slate-200 shrink-0" />
                      ) : (
                        <div className="h-11 w-11 rounded-xl bg-slate-200 flex items-center justify-center text-slate-500 font-black shrink-0">
                          {(bid.contractor?.name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="font-black text-slate-950 truncate">{bid.contractor?.name || "Contractor"}</p>
                          {bid.contractor?.isVerified && <ShieldCheck size={14} className="text-emerald-500 shrink-0" />}
                        </div>
                        <p className="text-xs text-slate-500 font-bold uppercase tracking-tight mt-1">
                          {bid.contractor?.trade || "Trade"}{bid.estimatedHours ? ` - ${bid.estimatedHours} hr` : ""}
                        </p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] text-slate-500 font-semibold">
                          {typeof bid.contractor?.rating === "number" && (
                            <span className="flex items-center gap-1">
                              <Star size={12} className="text-amber-400 fill-amber-400" />
                              {bid.contractor.rating.toFixed(1)}
                              {!!bid.contractor.reviewCount && ` (${bid.contractor.reviewCount})`}
                            </span>
                          )}
                          {!!bid.contractor?.hiredCount && <span>{bid.contractor.hiredCount} hired</span>}
                          {bid.contractor?.isLicensed && <span className="text-emerald-600">Licensed</span>}
                        </div>
                      </div>
                    </button>
                    <p className="text-2xl font-black text-emerald-600 shrink-0">A${Math.round(bid.amount)}</p>
                  </div>
                  {bid.contractor?.about && (
                    <p className="mt-3 text-xs leading-relaxed text-slate-500 line-clamp-2">{bid.contractor.about}</p>
                  )}
                  {(bid.includesMaterials || bid.materialsNote || bid.message) && (
                    <p className="mt-2 text-xs leading-relaxed text-slate-500">
                      {bid.includesMaterials ? "Includes materials. " : ""}
                      {bid.materialsNote || bid.message}
                    </p>
                  )}
                  <button
                    onClick={() => onSelectBid(job.id, bid.id)}
                    disabled={!!actionLoading}
                    className="mt-4 w-full rounded-xl bg-slate-950 px-4 py-3 text-xs font-black uppercase tracking-widest text-white hover:bg-emerald-600 transition-all disabled:opacity-50"
                  >
                    {actionLoading === job.id + "_" + bid.id + "_select" ? "Selecting..." : "Select this contractor"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 mb-6 bg-white/30 rounded-2xl p-6 border border-white/40 shadow-inner">
          <StatusTimeline status={job.status} />
        </div>

        {/* Parts Approval Section */}
        {job.parts && job.parts.length > 0 && (
          <div className="mt-8 p-6 rounded-[2rem] bg-slate-50 border border-slate-200">
             <div className="flex items-center justify-between mb-6">
                <h4 className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Mission Logistics: Parts</h4>
                <div className="h-6 w-6 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[10px] font-black text-slate-900">{job.parts.length}</div>
             </div>
             
             <div className="space-y-4">
                {job.parts.map(part => (
                   <div key={part.id} className="p-4 bg-white rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between group transition-all hover:border-emerald-200">
                      <div>
                         <p className="font-bold text-slate-950 text-sm">{part.name}</p>
                         <p className="text-[10px] font-black text-emerald-600 italic mt-0.5">A${part.price}</p>
                      </div>
                      
                      <div className="flex items-center gap-3">
                         {part.status === 'PENDING' ? (
                            <>
                               <button 
                                  onClick={() => onRejectPart(job.id, part.id)}
                                  disabled={!!actionLoading}
                                  className="px-4 py-2 rounded-xl bg-rose-50 text-rose-600 text-[10px] font-black uppercase tracking-widest hover:bg-rose-500 hover:text-white transition-all disabled:opacity-50"
                               >
                                  {actionLoading === job.id + "_" + part.id + "_reject" ? "..." : "Reject"}
                               </button>
                               <button 
                                  onClick={() => onApprovePart(job.id, part.id)}
                                  disabled={!!actionLoading}
                                  className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-200 disabled:opacity-50"
                               >
                                  {actionLoading === job.id + "_" + part.id + "_approve" ? "..." : "Approve"}
                                </button>
                            </>
                         ) : (
                            <div className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5 ${
                               part.status === 'APPROVED' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-rose-50 text-rose-600 border border-rose-100'
                            }`}>
                               {part.status === 'APPROVED' ? <CheckCircle2 size={10} /> : <AlertCircle size={10} />}
                               {part.status}
                            </div>
                         )}
                      </div>
                   </div>
                ))}
             </div>
          </div>
        )}

        {job.contractor && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-6 pt-6 border-t border-slate-100">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-slate-50/50 p-6 rounded-2xl border border-slate-100">
              <div className="flex items-center gap-4">
                <div className="relative h-14 w-14 shrink-0 rounded-2xl bg-white border border-slate-100 flex items-center justify-center text-emerald-600 shadow-sm">
                  <span className="text-xl font-black">{job.contractor.name.charAt(0)}</span>
                  <div className="absolute -bottom-1 -right-1 h-5 w-5 bg-white rounded-full flex items-center justify-center text-emerald-500 shadow-sm">
                    <ShieldCheck size={12} fill="currentColor" className="text-white" />
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-slate-950 leading-none">{job.contractor.name}</p>
                    {job.contractor.isVerified && (
                      <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded-md border border-emerald-100">Verified Pro</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 font-bold uppercase tracking-tight mt-1.5">
                    {job.contractor.trade} • {job.contractor.businessType}
                    {job.contractor.rating ? ` • ⭐ ${job.contractor.rating}` : ""}
                  </p>
                </div>
              </div>
              {["assigned", "awaiting_customer_confirmation", "completed"].includes(job.status) && (
                <button
                  onClick={() => setChatOpen(true)}
                  className="rounded-xl bg-emerald-600 px-5 py-3 text-xs font-black uppercase tracking-widest text-white transition-colors hover:bg-emerald-700"
                >
                  Message contractor
                </button>
              )}
            </div>
          </motion.div>
        )}

        {["assigned", "awaiting_customer_confirmation"].includes(job.status) && job.customerLocation && (
          <div className="mt-8">
            <LiveTrackingMap 
              jobId={job.id} 
              destination={job.customerLocation}
              contractorName={job.contractor?.name || "Pro"}
              contractorPhone={job.contractor?.phones?.[0]?.number}
            />
          </div>
        )}

        {job.status === "completed" && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 p-6 rounded-2xl bg-emerald-50 border border-emerald-100">
            <div className="flex items-start gap-3 mb-4">
              <Sparkles size={18} className="text-emerald-600" />
              <div>
                <p className="text-sm font-bold text-emerald-950">Maintenance Completed Successfully</p>
                <p className="text-xs text-emerald-700 font-medium mt-1">Review the work quality to release payment and finalize the record.</p>
              </div>
            </div>
            <button 
              onClick={() => onConfirm(job.id)} 
              disabled={actionLoading === job.id + "_confirm"}
              className="w-full py-4 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 disabled:opacity-50 transition-all shadow-lg shadow-emerald-200 flex items-center justify-center gap-2"
            >
              {actionLoading === job.id + "_confirm" ? "Confirming..." : "Finalize & Leave Review"}
              <ChevronRight size={16} />
            </button>
          </motion.div>
        )}

        {isDone && (
          <div className="mt-6 flex items-center gap-2 p-4 rounded-2xl bg-white/50 border border-slate-100">
            <CheckCircle2 size={16} className="text-emerald-500" />
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">
              Service Archived • {job.reviews?.[0] ? `Rated ${job.reviews[0].rating}/5` : "Payment Released"}
            </p>
          </div>
        )}
      </div>
      {chatOpen && <JobChat jobId={job.id} onClose={() => setChatOpen(false)} />}
      {viewingContractor && <ContractorProfileModal contractor={viewingContractor} onClose={() => setViewingContractor(null)} />}
    </div>
  );
}

function StatusTimeline({ status }: { status: string }) {
  const steps = [
    { key: "requested", label: "Requested" }, 
    { key: "matching", label: "Matching" }, 
    { key: "in_progress", label: "In Progress" }, 
    { key: "finalized", label: "Finalized" }
  ];
  
  const statusToIdx: Record<string, number> = {
    "priced": 1,
    "manual_dispatch_required": 1,
    "assigned": 2,
    "awaiting_customer_confirmation": 2.5,
    "completed": 3,
    "reviewed": 3,
  };

  const currentProgress = statusToIdx[status] ?? 0;
  
  return (
    <div className="flex items-center w-full px-2">
      {steps.map((s, i) => {
        const isCompleted = i < Math.floor(currentProgress);
        const isActive = i === Math.floor(currentProgress);
        const isPast = i < currentProgress;
        
        return (
          <div key={s.key} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center relative z-10">
              <motion.div 
                initial={false}
                animate={isActive ? { scale: 1.2 } : { scale: 1 }}
                className={`h-5 w-5 rounded-full flex items-center justify-center border-2 transition-colors duration-500 ${
                  isActive ? "bg-emerald-600 border-emerald-400 shadow-lg shadow-emerald-200" : 
                  isPast ? "bg-emerald-500 border-emerald-500" : 
                  "bg-white border-slate-200"
                }`}
              >
                {isPast && !isActive && <CheckCircle2 size={12} className="text-white" />}
                {isActive && <div className="h-1.5 w-1.5 bg-white rounded-full animate-ping" />}
              </motion.div>
              <span className={`absolute -bottom-8 text-[10px] font-bold uppercase tracking-tight whitespace-nowrap transition-colors duration-500 ${
                isActive ? "text-emerald-600" : isPast ? "text-slate-500" : "text-slate-300"
              }`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className="flex-1 h-1.5 mx-0 bg-slate-100 rounded-full overflow-hidden">
                <motion.div 
                  initial={{ width: "0%" }}
                  animate={{ 
                    width: currentProgress > i + 1 ? "100%" : 
                           currentProgress > i ? `${(currentProgress - i) * 100}%` : "0%" 
                  }}
                  transition={{ duration: 1, ease: "easeInOut" }}
                  className="h-full bg-emerald-500"
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
